import { ROLE_ACTIONS } from '@reos/policy';
import { filterMenu, type AccessPolicy } from '@redhand/access';
import { NAV_SECTIONS, type Blok1NavItem } from './nav.js';

export interface MenuItem {
  readonly id: string;
  readonly label: string;
  readonly path: string;
  readonly action: string;
  readonly icon?: string;
}

/**
 * Odvozeno primo z NAV_SECTIONS (jediny zdroj pravdy pro menu, BLOK 2.5
 * rozhodnuti orchestratora #1: „Duplicitni menu nesmi existovat"). Polozka
 * ma `action`, jen kdyz je citliva (audit, schvaleni, uzivatele a role,
 * provize) — ostatni jsou zatim viditelne vsem rolim.
 */
export const MENU_DEFINITIONS: readonly MenuItem[] = NAV_SECTIONS.flatMap((section) => section.items)
  .filter((item): item is Blok1NavItem & { readonly action: string } => item.action !== undefined)
  .map((item) => ({ id: item.id, label: item.label, path: item.path, action: item.action }));

/**
 * REOS adapter na @redhand/access: granty roli prichazeji z @reos/policy
 * (ROLE_ACTIONS), entitlements v0 = REOS tenant ma vsechny moduly (menu
 * polozky nemaji `module`, kontroluji se jen role — osa entitlements se
 * zapne, az budou moduly per tenant).
 */
const REOS_ACCESS_POLICY: AccessPolicy = {
  entitlements: [],
  grants: { ...ROLE_ACTIONS },
};

/**
 * Filter menu by roles — same signature as before, now delegated to the
 * shared @redhand/access kit (filterMenu).
 */
export function filterMenuByRoles(roles: readonly string[], items: readonly MenuItem[] = MENU_DEFINITIONS): readonly MenuItem[] {
  return filterMenu(items, { roles }, REOS_ACCESS_POLICY);
}

/**
 * Single-item check — useful for tests to assert invisibility.
 */
export function canSeeMenuItem(roles: readonly string[], action: string): boolean {
  return filterMenuByRoles(roles, [{ id: 'x', label: 'x', path: '/x', action }]).length === 1;
}

/**
 * Viditelnost jedne nav polozky: bez `action` viditelna vsem (BLOK 2.5
 * rozhodnuti #2), s `action` plati stejna role-gating logika jako drive.
 * Krok UXA1: `ownerAdminOnly` je dalsi, nezavisla podminka — polozka v
 * sekci „Nastavení a provoz" potrebuje owner/admin NAD RAMEC sveho `action`
 * (ten muze mit grant i jina role, napr. `legal` u `audit.log.read`).
 * Pouziva layout.tsx (sidebar) i command-palette.tsx (Ctrl+K), aby obe
 * mista videla presne stejnou sadu polozek.
 */
export function isNavItemVisible(
  roles: readonly string[],
  item: Pick<Blok1NavItem, 'action' | 'ownerAdminOnly'>,
): boolean {
  if (item.ownerAdminOnly === true && !isOwnerOrAdmin(roles)) return false;
  return item.action === undefined || canSeeMenuItem(roles, item.action);
}

/**
 * Spravce kancelare, ne makler. JEDNO misto pro cely admin: pouziva ho
 * `isNavItemVisible` (polozky „Nastavení a provoz") i obrazovky, ktere maji
 * sekci jen pro spravce — napr. interni diagnostika „Ladění pilotu" na
 * `/k-doplneni` (krok FUNK1 bod 3). Dva ruzne vycty roli by se rozesly.
 */
export function isOwnerOrAdmin(roles: readonly string[]): boolean {
  return roles.some((role) => role === 'owner' || role === 'admin');
}
